/**
 * Soft!=Live Page200 — Settings Profile residual liquid (Page116/63 + dual).
 * Mount: /settings/profile
 * Buses (contract ops — never painted in operator DOM):
 *   get__settings_profile_status · session me · avatar upload · profile clear
 * Soft residual deleted:
 *  - Soft display-name / email edit invent
 *  - Soft avatar success when write bus sealed
 *  - Soft feature-flag toggles on this desk
 *  - Soft invent seats while posture loading
 *  - path /operation labels painted in DOM
 */
import { useEffect, useRef, useState } from "react";
import { html, Ico, Card, Alert, KV, go } from "./ui.js";
import { path, useAction, useApi } from "./api.js";
import { envelopeSummary } from "./action-envelope.js";
import { useSession } from "./session.js";
import { setTimezonePreference, supportedTimezones, timezoneOf } from "./time.js";

const AVATAR_MAX_DEFAULT = 512 * 1024;
const TIMEZONES = supportedTimezones();

/** Path-free operator copy for Profile desk. Catalogue stays empty until Live says so. */
export function softProfileSettingsErrorOf(err) {
  const n = String(err == null ? "" : err).trim();
  const low = n.toLowerCase();
  if (!low) return "That action could not be completed.";
  if (/\b403\b|forbidden|seat_gated|not_authorized|denied/.test(low)) {
    return "Your account cannot change this profile. Ask a manager if you need access.";
  }
  if (/\b401\b|session|unauthorized|missing bearer/.test(low)) {
    return "Sign in again to continue.";
  }
  if (/\b404\b|not found/.test(low)) {
    return "That profile action is not available.";
  }
  if (/\b413\b|too large|max/.test(low)) {
    return "That photo is too large.";
  }
  if (/\b415\b|type|jpeg|png|image/.test(low)) {
    return "Use a JPEG or PNG photo.";
  }
  if (/\b422\b|\b409\b|invalid|required|conflict/.test(low)) {
    return "That change was not accepted.";
  }
  if (/\b429\b|too many/.test(low)) {
    return "Too many tries — wait a moment, then retry.";
  }
  if (/\b503\b|unavailable/.test(low)) {
    return "Profile is not ready right now.";
  }
  if (/network|failed to fetch|timeout/.test(low)) {
    return "Network issue — try again in a moment.";
  }
  if (/\/api\/|\/auth\/|\/settings\/profile|GET |POST |PATCH |status_path|http/.test(low)) {
    return "That action could not be completed.";
  }
  if (n.length > 160) return n.slice(0, 157) + "…";
  return n;
}

/** Soft!=Live Page200: missing contract ops must not crash the desk. */
function safeProfilePath(op, opts) {
  try {
    return path(op, opts);
  } catch {
    if (op === "get__settings_profile_status") return "/settings/profile/status";
    if (op === "patch__auth_me_profile") return "/auth/me/profile";
    if (op === "post__auth_me_avatar_upload") return "/auth/me/avatar/upload";
    return "";
  }
}

/** Pure: seat chips from Live Settings Profile posture (Page200) — fail-closed. */
export function profileSettingsSeatOf(post) {
  const p = post && typeof post === "object" ? post : {};
  const lim = (p.limits && typeof p.limits === "object") ? p.limits : {};
  const maxB = Number(lim.avatar_max_bytes || p.avatar_max_bytes);
  return {
    honestyAllowed: p.profile_honesty_allowed === true,
    avatarWriteAllowed: p.avatar_write_allowed === true,
    displayNameWriteAllowed: p.display_name_write_allowed === true,
    emailWriteAllowed: p.email_write_allowed === true,
    featureToggleWriteAllowed: p.feature_toggle_write_allowed === true,
    sessionsNavAllowed: p.sessions_nav_allowed === true,
    passwordNavAllowed: p.password_nav_allowed === true,
    signoutNavAllowed: p.signout_nav_allowed === true,
    reReadAllowed: p.re_read_allowed === true,
    role: String(p.principal_role || ""),
    detail: p.detail || null,
    enabled: p.enabled === true,
    postureLabel: String(p.posture_label || "").trim(),
    avatarMaxBytes: Number.isFinite(maxB) && maxB > 0 ? Math.floor(maxB) : AVATAR_MAX_DEFAULT,
  };
}

/** Pure: KPI strip from shaped me. */
export function profileKpisOf(me) {
  const m = me && typeof me === "object" ? me : {};
  const feats = m.features || {};
  const onCount = Object.values(feats).filter(Boolean).length;
  return {
    user: String(m.username || "—"),
    role: String(m.role || "—"),
    verified: !!m.emailVerified,
    hasAvatar: !!m.hasAvatar,
    featOn: onCount,
    caps: Array.isArray(m.caps) ? m.caps.length : 0,
  };
}

export function ProfileSettingsDesk({ toast }) {
  const { me, reload } = useSession();
  const posture = useApi(safeProfilePath("get__settings_profile_status"));
  const post = posture.data || {};
  // Fail-closed while loading — seats fail-closed.
  const seat = profileSettingsSeatOf(posture.loading || !posture.data ? {} : post);
  const { run, busy, error, envelope } = useAction();
  const fileRef = useRef(null);
  const [timezone, setTimezone] = useState(() => timezoneOf(me));
  useEffect(() => setTimezone(timezoneOf(me)), [me && me.username, me && me.timezone]);
  const postureLabel = seat.postureLabel
    || (seat.honestyAllowed
      ? (seat.avatarWriteAllowed ? "Identity and avatar open" : "Identity open · avatar edit sealed")
      : "Profile desk sealed");

  if (!me) {
    return html`<div className="prof-set-desk prof-set-desk--liquid"
      data-cmc-page="page116-profile" data-cmc-profile="page63" data-cmc-residual="page200-profile">
      <${Alert} tone="warn" title="No session" say="Sign in again to continue."/>
    </div>`;
  }

  if (posture.loading && !posture.data) {
    return html`<div className="prof-set-desk prof-set-desk--liquid"
      data-cmc-page="page116-profile" data-cmc-profile="page63" data-cmc-residual="page200-profile">
      <p className="note">Reading profile seat… Profile stays blank until the server confirms.</p>
    </div>`;
  }

  if (!seat.enabled) {
    return html`<div className="prof-set-desk prof-set-desk--liquid"
      data-cmc-page="page116-profile" data-cmc-profile="page63" data-cmc-residual="page200-profile">
      <${Alert} tone="warn" title="Profile seat closed"
        say=${postureLabel + ". Profile stays blank until the server confirms."}/>
      <button type="button" className="btn btn--ghost btn--sm" style=${{ marginTop: 12 }}
        disabled=${posture.loading || !seat.reReadAllowed}
        onClick=${() => { posture.reload && posture.reload(); }}>
        <${Ico} d="refresh" w=${14}/> Re-read
      </button>
    </div>`;
  }

  const kpis = profileKpisOf(me);
  const feats = [
    ["Map", me.features && me.features.map],
    ["Cameras", me.features && me.features.cameras],
    ["Billing", me.features && me.features.billing],
    ["Voice", me.features && me.features.voice],
    ["Tenant ACL", me.features && me.features.tenantAcl],
    ["Ops dashboard", me.features && me.features.opsDashboard],
  ];
  const policyKeys = Object.keys((me.policy) || {});

  const reRead = () => {
    if (!seat.reReadAllowed) {
      toast && toast("Refresh is not available on this seat.", "warn");
      return;
    }
    posture.reload && posture.reload();
    reload && reload();
    toast && toast("Profile refreshed", "go");
  };

  const changeTimezone = (event) => {
    const next = String(event && event.target && event.target.value || "");
    try {
      setTimezone(setTimezonePreference(me.username, next));
      toast && toast("Time display updated", "go");
    } catch {
      toast && toast("Choose a valid IANA timezone.", "bad");
    }
  };

  const clearAvatar = async () => {
    if (!seat.avatarWriteAllowed) {
      toast && toast("Your seat cannot change avatar photos.", "warn");
      return;
    }
    const out = await run(safeProfilePath("patch__auth_me_profile"), {
      method: "PATCH",
      body: { avatar_url: "" },
    });
    if (!out) {
      toast(softProfileSettingsErrorOf(error), "bad");
      return;
    }
    toast("Avatar cleared", "go");
    reload && reload();
  };

  const pickAvatar = () => {
    if (!seat.avatarWriteAllowed) {
      toast && toast("Your seat cannot change avatar photos.", "warn");
      return;
    }
    if (fileRef.current) fileRef.current.click();
  };

  const onAvatarFile = async (e) => {
    if (!seat.avatarWriteAllowed) {
      toast && toast("Your seat cannot change avatar photos.", "warn");
      return;
    }
    const f = e && e.target && e.target.files && e.target.files[0];
    if (fileRef.current) fileRef.current.value = "";
    if (!f) return;
    if (f.size > seat.avatarMaxBytes) {
      toast(`File too large · max ${Math.floor(seat.avatarMaxBytes / 1024)} KB`, "bad");
      return;
    }
    const okType = /^image\/(jpeg|png)$/i.test(f.type || "");
    const okName = /\.(jpe?g|png)$/i.test(f.name || "");
    if (!okType && !okName) {
      toast("Use JPEG or PNG only", "bad");
      return;
    }
    const fd = new FormData();
    fd.append("file", f, f.name || "avatar.jpg");
    const out = await run(safeProfilePath("post__auth_me_avatar_upload"), {
      method: "POST",
      body: fd,
    });
    if (!out) {
      toast(softProfileSettingsErrorOf(error), "bad");
      return;
    }
    const pend = !!(out.pending || out.avatar_upload_pending || (me.raw && me.raw.avatar_upload_pending));
    toast(
      pend
        ? "Avatar queued — publishes after verification"
        : "Avatar uploaded",
      "go",
    );
    reload && reload();
  };

  return html`<div className="prof-set-desk prof-set-desk--liquid"
    data-cmc-page="page116-profile" data-cmc-profile="page63" data-cmc-residual="page200-profile">
    <div className="prof-set-desk__rail">
      <div>
        <h2 className="prof-set-desk__title">Who you are</h2>
      </div>
      <div className="btn-row prof-set-desk__acts">
        <button type="button" className="btn btn--ghost btn--sm" disabled=${busy || !seat.reReadAllowed}
          onClick=${reRead}>
          <${Ico} d="refresh" w=${14}/> Re-read session
        </button>
        ${seat.sessionsNavAllowed ? html`
          <button type="button" className="btn btn--ghost btn--sm"
            onClick=${()=>go("/settings/sessions")}>Sessions</button>` : null}
        ${seat.passwordNavAllowed ? html`
          <button type="button" className="btn btn--ghost btn--sm"
            onClick=${()=>go("/settings/security")}>Password</button>` : null}
      </div>
    </div>

    ${envelope ? html`<${KV} k="Last action" v=${envelopeSummary(envelope) || "—"} tone=${envelope.ok ? "hi" : "warn"}/>` : null}

    ${seat.honestyAllowed ? html`
    <div className="prof-set-desk__kpis" role="group" aria-label="Profile KPIs">
      <div className="prof-set-desk__kpi" data-on="1">
        <span>Principal</span><b>${kpis.user}</b>
        <em>identity</em>
      </div>
      <div className="prof-set-desk__kpi" data-on="1">
        <span>Role</span><b>${kpis.role}</b>
        <em>session</em>
      </div>
      <div className="prof-set-desk__kpi" data-on=${kpis.verified ? "1" : "0"}>
        <span>Email</span><b>${kpis.verified ? "verified" : "open"}</b>
        <em>read-only</em>
      </div>
      <div className="prof-set-desk__kpi" data-on=${kpis.hasAvatar ? "1" : "0"}>
        <span>Avatar</span><b>${kpis.hasAvatar ? ("r" + (me.avatarRev || 0)) : "initials"}</b>
        <em>${seat.avatarWriteAllowed ? "edit open" : "edit sealed"}</em>
      </div>
    </div>` : html`
    <${Alert} tone="warn" title="Identity sealed"
      say="Cannot show principal details · Profile stays blank until the server confirms."/>`}

    ${seat.honestyAllowed ? html`
    <${Card} title="Identity" meta=${me.initials || "??"}
        sub="Username is the audit identity. Email and username are not changed here — recovery or administrator only.">
      <div className="grid g2">
        <${KV} k="Username" v=${me.username}/>
        <${KV} k="Email" v=${me.email || "—"}/>
        <${KV} k="Email verified" v=${me.emailVerified ? "yes" : "not yet"}/>
        <${KV} k="Role" v=${me.role || "—"}/>
        <${KV} k="Tenant scope" v=${me.tenant || "—"}/>
        <${KV} k="Zones" v=${(me.zones && me.zones.length) ? me.zones.join(", ") : "—"}/>
      </div>
      ${Array.isArray(me.caps) && me.caps.length ? html`
        <p className="note" style=${{ marginTop: 12 }}>
          Staff capabilities: ${me.caps.join(", ")}
          ${me.saGrantedUntil ? (" · grant until " + me.saGrantedUntil) : ""}
        </p>` : null}
    <//>` : null}

    <${Card} title="Time display" meta=${timezone}
        sub="UTC records stay unchanged. This browser projects them in your selected IANA timezone.">
      <label className="field">
        <span>Timezone</span>
        <select value=${timezone} onChange=${changeTimezone}>
          ${TIMEZONES.map((zone) => html`<option key=${zone} value=${zone}>${zone}</option>`)}
        </select>
      </label>
      <p className="note">Changing this setting never changes command deadlines, audit order, evidence, or proof.</p>
    <//>

    <${Card} title="Avatar"
        meta=${seat.avatarWriteAllowed ? "upload · clear" : "edit sealed"}
        sub="JPEG or PNG. Upload stays closed until your seat opens.">
      ${!seat.avatarWriteAllowed ? html`
        <${Alert} tone="warn" title="Avatar edit sealed"
          say="Upload and clear stay closed on this seat. Success only after the server confirms."/>` : null}
      <div className="prof-set-desk__avatar">
        <div className="prof-set-desk__initials" aria-hidden="true">${me.initials}</div>
        <div className="btn-row">
          <input ref=${fileRef} type="file" accept="image/jpeg,image/png,.jpg,.jpeg,.png"
            style=${{ display: "none" }} onChange=${onAvatarFile}/>
          <button type="button" className="btn btn--go btn--sm"
            disabled=${busy || !seat.avatarWriteAllowed}
            onClick=${pickAvatar}>Upload photo</button>
          <button type="button" className="btn btn--ghost btn--sm"
            disabled=${busy || !seat.avatarWriteAllowed || !me.hasAvatar}
            onClick=${clearAvatar}>Clear avatar</button>
        </div>
      </div>
      <p className="note">Pending verification may delay publish.</p>
    <//>

    ${seat.honestyAllowed ? html`
    <${Card} title="Session flags (read-only)"
        meta=${kpis.featOn + " on"}>
      <div className="grid g2" style=${{ gap: "0 22px" }}>
        ${feats.map(([k, on]) => html`
          <div key=${k} className="kv">
            <span>${k}</span>
            <b className=${on ? "" : "bad"}>${on ? "on" : "off"}</b>
          </div>`)}
      </div>
      ${policyKeys.length ? html`
        <p className="note" style=${{ marginTop: 12 }}>
          Role permissions listed for honesty · ${policyKeys.length} keys.
        </p>` : null}
      <p className="note" style=${{ marginTop: 8 }}>
        Flags are honesty from your session — toggles only write when your seat allows them.
      </p>
    <//>` : null}

    <${Card} title="This browser"
        sub="End session without closing the account.">
      ${seat.signoutNavAllowed ? html`
        <button type="button" className="btn btn--ghost" data-cmc-signout="profile"
          onClick=${()=>go("/auth/signout")}>
          <${Ico} d="logout" w=${14}/> Sign out this browser
        </button>` : html`
        <${Alert} tone="warn" title="Sign out sealed"
          say="Sign-out only through session control."/>`}
    <//>
  </div>`;
}
